import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/Dialog";
import Button from "@/components/ui/Button";
import CreatableSelect from "@/components/ui/CreatableSelect";
import { useCanManageAssets } from "@/hooks/useCanManageAssets";
import {
  AssetView,
  AssetCondition,
  AssetCategoryView,
  AssetLocationView,
  CreateAssetInput,
  UpdateAssetInput,
  getAssetLocations,
  createAssetLocation,
} from "../../../../api/General/assets";

interface AssetFormModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (input: CreateAssetInput | UpdateAssetInput) => Promise<void>;
  asset?: AssetView | null;
  categories: AssetCategoryView[];
  saving: boolean;
}

const AssetFormModal: React.FC<AssetFormModalProps> = ({
  open,
  onClose,
  onSave,
  asset,
  categories,
  saving,
}) => {
  const { t } = useTranslation();
  const canManageAssets = useCanManageAssets();
  const isEditing = !!asset;

  // The asset's identity is its Level-3 model, picked via three cascading dropdowns (Group -> Type -> Model)
  // instead of a free-text name; l1Id/l2Id only narrow the Model options and are never submitted.
  const [l1Id, setL1Id] = useState<number>(0);
  const [l2Id, setL2Id] = useState<number>(0);
  const [categoryId, setCategoryId] = useState<number>(0);
  const [serialNumber, setSerialNumber] = useState("");
  const [serialNumbers, setSerialNumbers] = useState<string[]>([]);
  const [newSerial, setNewSerial] = useState("");
  const [purchasePrice, setPurchasePrice] = useState("");
  const [purchaseDate, setPurchaseDate] = useState("");
  const [warrantyExpiry, setWarrantyExpiry] = useState("");
  const [condition, setCondition] = useState<AssetCondition>(AssetCondition.Good);
  const [storageLocationId, setStorageLocationId] = useState<number | undefined>(undefined);
  const [quantity, setQuantity] = useState("1");
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [locations, setLocations] = useState<AssetLocationView[]>([]);

  const categoryById = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);
  const l1s = useMemo(() => categories.filter((c) => c.level === 1), [categories]);
  const l2s = useMemo(() => categories.filter((c) => c.level === 2 && c.parentId === l1Id), [categories, l1Id]);
  const l3s = useMemo(() => categories.filter((c) => c.level === 3 && c.parentId === l2Id), [categories, l2Id]);
  const hasModels = categories.some((c) => c.level === 3);

  useEffect(() => {
    (async () => {
      const result = await getAssetLocations();
      if (result.errors.length === 0) {
        setLocations(result.payload);
      }
    })();
  }, []);

  useEffect(() => {
    if (asset) {
      // Walk L3 -> L2 -> L1 so the three selects open pre-populated for editing.
      const l3 = categoryById.get(asset.categoryId);
      const l2 = l3?.parentId != null ? categoryById.get(l3.parentId) : undefined;
      setL1Id(l2?.parentId ?? 0);
      setL2Id(l2?.id ?? 0);
      setCategoryId(asset.categoryId);
      setSerialNumber(asset.serialNumber ?? "");
      setSerialNumbers([]);
      setNewSerial("");
      setPurchasePrice(asset.purchasePrice != null ? String(asset.purchasePrice) : "");
      setPurchaseDate(asset.purchaseDate ? asset.purchaseDate.split("T")[0] : "");
      setWarrantyExpiry(asset.warrantyExpiry ? asset.warrantyExpiry.split("T")[0] : "");
      setCondition(asset.condition);
      setStorageLocationId(asset.storageLocationId);
      setQuantity("1");
      setNotes(asset.notes ?? "");
    } else {
      setL1Id(0);
      setL2Id(0);
      setCategoryId(0);
      setSerialNumber("");
      setSerialNumbers([]);
      setNewSerial("");
      setPurchasePrice("");
      setPurchaseDate("");
      setWarrantyExpiry("");
      setCondition(AssetCondition.Good);
      setStorageLocationId(undefined);
      setQuantity("1");
      setNotes("");
    }
    setErrors({});
  }, [asset, open, categories, categoryById]);

  // CO-160/CO-197: mirrors the server's MaxAssetQuantityPerEntry. Over it the server REFUSES
  // (it no longer silently clamps), so the form has to refuse too rather than post a doomed request.
  const MAX_QUANTITY = 500;

  const quantityNumber = Number(quantity) || 1;
  const isMultiUnit = !isEditing && quantityNumber > 1;

  // Adds the location to the company list and keeps the user inside the asset form.
  const handleCreateLocation = async (name: string): Promise<number | null> => {
    const result = await createAssetLocation({ name });
    if (result.errors.length > 0 || !result.payload) return null;
    setLocations((prev) => [...prev, result.payload]);
    return result.payload.id;
  };

  const handleL1Change = (id: number) => {
    setL1Id(id);
    setL2Id(0);
    setCategoryId(0);
  };

  const handleL2Change = (id: number) => {
    setL2Id(id);
    setCategoryId(0);
  };

  const addSerial = () => {
    const value = newSerial.trim();
    if (!value) return;
    if (serialNumbers.length >= quantityNumber) {
      setErrors((prev) => ({ ...prev, serialNumbers: t("assets.serialCountExceedsQuantity") }));
      return;
    }
    if (serialNumbers.some((s) => s.toLowerCase() === value.toLowerCase())) {
      setErrors((prev) => ({ ...prev, serialNumbers: t("assets.serialDuplicate") }));
      return;
    }
    setSerialNumbers([...serialNumbers, value]);
    setNewSerial("");
    setErrors((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => k !== "serialNumbers")));
  };

  const removeSerial = (index: number) => {
    setSerialNumbers(serialNumbers.filter((_, i) => i !== index));
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!categoryId) errs.categoryId = t("assets.categoryRequired");
    if (!isEditing && quantityNumber > MAX_QUANTITY) errs.quantity = t("assets.quantityMax", { max: MAX_QUANTITY });
    if (isMultiUnit && serialNumbers.length > quantityNumber) errs.serialNumbers = t("assets.serialCountExceedsQuantity");
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const input: CreateAssetInput | UpdateAssetInput = {
      ...(isEditing ? { id: asset!.id } : {}),
      categoryId,
      serialNumber: isMultiUnit ? undefined : (serialNumber.trim() || undefined),
      ...(isMultiUnit ? { serialNumbers } : {}),
      purchasePrice: purchasePrice ? Number(purchasePrice) : undefined,
      purchaseDate: purchaseDate || undefined,
      warrantyExpiry: warrantyExpiry || undefined,
      condition,
      storageLocationId,
      ...(isEditing ? {} : { quantity: quantityNumber }),
      notes: notes.trim() || undefined,
    } as CreateAssetInput | UpdateAssetInput;

    await onSave(input);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="default">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t("assets.editAsset") : t("assets.addAsset")}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <DialogBody className="space-y-4">
            {/* Category: Group -> Type -> Model. The asset's Name is the Model (Level-3) category. */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t("assets.categoryGroup")} *</label>
                <select
                  value={l1Id}
                  onChange={(e) => handleL1Change(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                  data-testid="asset-l1-select"
                >
                  <option value={0} disabled>{t("assets.selectCategoryGroup")}</option>
                  {l1s.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t("assets.categoryType")} *</label>
                <select
                  value={l2Id}
                  onChange={(e) => handleL2Change(Number(e.target.value))}
                  disabled={!l1Id}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed"
                  data-testid="asset-l2-select"
                >
                  <option value={0} disabled>{t("assets.selectCategoryType")}</option>
                  {l2s.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t("assets.categoryModel")} *</label>
                <select
                  value={categoryId}
                  onChange={(e) => setCategoryId(Number(e.target.value))}
                  disabled={!l2Id}
                  className={`w-full px-3 py-2 border rounded-lg text-sm bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed ${errors.categoryId ? "border-destructive" : "border-border"}`}
                  data-testid="asset-l3-select"
                >
                  <option value={0} disabled>{t("assets.selectCategoryModel")}</option>
                  {l3s.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
            </div>
            {!hasModels && (
              <p className="text-xs text-muted-foreground -mt-2">{t("assets.noModelCategoriesHint")}</p>
            )}
            {errors.categoryId && <p className="text-destructive text-xs -mt-2">{errors.categoryId}</p>}

            {/* Quantity (create only) */}
            {!isEditing && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t("assets.quantity")}</label>
                <input
                  type="number"
                  min="1"
                  max={MAX_QUANTITY}
                  step="1"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                  data-testid="asset-quantity-input"
                />
                {errors.quantity && <p className="text-destructive text-xs mt-1" data-testid="asset-quantity-error">{errors.quantity}</p>}
              </div>
            )}

            {/* Serial Number(s) */}
            {isMultiUnit ? (
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t("assets.serialNumbers")}</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {serialNumbers.map((s, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-md text-sm">
                      {s}
                      <button
                        type="button"
                        onClick={() => removeSerial(i)}
                        aria-label={t("assets.removeSerial")}
                        className="text-muted-foreground hover:text-destructive"
                        data-testid={`asset-serial-chip-remove-${i}`}
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newSerial}
                    onChange={(e) => setNewSerial(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addSerial())}
                    placeholder={t("assets.addSerialPlaceholder")}
                    className="flex-1 px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                    data-testid="asset-serial-input"
                  />
                  <Button type="button" variant="secondary" size="sm" onClick={addSerial}>
                    {t("assets.add")}
                  </Button>
                </div>
                <p className="text-muted-foreground text-xs mt-1">{t("assets.quantityHint")}</p>
                {errors.serialNumbers && <p className="text-destructive text-xs mt-1">{errors.serialNumbers}</p>}
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t("assets.serialNumber")}</label>
                <input
                  type="text"
                  value={serialNumber}
                  onChange={(e) => setSerialNumber(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                  data-testid="asset-serial-input"
                />
              </div>
            )}

            {/* Purchase Price & Date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t("assets.purchasePricePerItem")}</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={purchasePrice}
                  onChange={(e) => setPurchasePrice(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                  data-testid="asset-price-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t("assets.purchaseDate")}</label>
                <input
                  type="date"
                  value={purchaseDate}
                  onChange={(e) => setPurchaseDate(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                  data-testid="asset-purchase-date-input"
                />
              </div>
            </div>

            {/* Warranty Expiry */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">{t("assets.warrantyExpiry")}</label>
              <input
                type="date"
                value={warrantyExpiry}
                onChange={(e) => setWarrantyExpiry(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                data-testid="asset-warranty-input"
              />
            </div>

            {/* Condition */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">{t("assets.condition")}</label>
              <select
                value={condition}
                onChange={(e) => setCondition(e.target.value as AssetCondition)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                data-testid="asset-condition-select"
              >
                <option value={AssetCondition.Excellent}>{t("assets.conditionExcellent")}</option>
                <option value={AssetCondition.Good}>{t("assets.conditionGood")}</option>
                <option value={AssetCondition.Fair}>{t("assets.conditionFair")}</option>
                <option value={AssetCondition.Poor}>{t("assets.conditionPoor")}</option>
              </select>
            </div>

            {/* Storage Location */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">{t("assets.location")}</label>
              <CreatableSelect
                value={storageLocationId}
                onChange={setStorageLocationId}
                // Hidden for anyone the CreateAssetLocation policy would reject rather than shown and left to fail.
                onCreate={canManageAssets ? handleCreateLocation : undefined}
                options={locations.map((loc) => ({ value: loc.id, label: loc.name }))}
                placeholder={t("assets.selectOrAddLocation")}
                // Location is optional, so "no location" has to be something the user can pick on
                // purpose — CO-522 removed the accidental clear that used to stand in for it.
                clearable
                clearLabel={t("assets.unassignedLocation")}
                disabled={saving}
                data-testid="asset-location-select"
              />
              <p className="text-muted-foreground text-xs mt-1">{t("assets.locationOptionalHint")}</p>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">{t("assets.notes")}</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary resize-none"
                data-testid="asset-notes-input"
              />
            </div>
          </DialogBody>

          {/* Actions */}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              data-testid="asset-form-cancel"
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              loading={saving}
              data-testid="asset-form-save"
            >
              {saving ? t("common.saving") : isEditing ? t("common.save") : t("assets.addAsset")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AssetFormModal;
